
/**
 * Module dependencies.
 */

import { Name as MenuName, useMenu } from 'src/core/menu';
import {
  NavThemeProps,
  borderOpacity,
  navDuration,
  navTransition
} from './nav-theme';
import { NavbarTheme } from 'src/types/navbar';
import { color, media, units } from 'src/styles/utils';
import { ifProp, switchProp, theme } from 'styled-tools';
import { isInternalRoute } from './nav-element';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useNavbarThemeContext } from 'src/context/navbar-theme/context';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import HamburgerMenu from './hamburger-menu';
import MegaMenu from './mega-menu';
import MobileMenu from './mobile-menu';
import NavButton from './nav-button';
import NavbarButton from './navbar-button';
import PreNavbar from './pre-navbar';
import React, {
  FocusEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import RouterLink from 'src/components/core/links/router-link';
import Search from 'src/components/search';
import Svg from 'src/components/core/svg';
import logoIcon from 'src/assets/svg/logo.svg';
import map from 'lodash/map';
import searchIcon from 'src/assets/svg/search.svg';
import styled, { css } from 'styled-components';
import throttle from 'lodash/throttle';
import useBodyScroll from 'src/hooks/use-body-scroll';
import useNavbarCards from 'src/api/app/navbar-cards/use-navbar-cards';

/**
 * Scroll threshold.
 *
 * The design asks for the navbar to go full color on the first scroll down,
 * the offset only keeps it from flickering on elastic scroll.
 */

const scrollThreshold = 8;

/**
 * Mega menu delays.
 *
 * Opening waits a moment so that sweeping the cursor across the navbar does
 * not flash every panel on the way to the intended one. Switching between
 * panels while one is already open is immediate.
 */

const closeDelay = 180;
const openDelay = 120;

/**
 * Mega menu panel id.
 *
 * There is a single panel that swaps contents, so every nav button points at
 * it and the reference is always valid.
 */

const megaMenuId = 'navbar-mega-menu';

/**
 * `Nav` styled component.
 */

const Nav = styled.nav<NavThemeProps>`
  border-bottom: 1px solid transparent;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  transition: ${navTransition};
  transition-property: background-color, border-color;
  z-index: ${theme('zIndex.navbar')};

  ${switchProp('colorTheme', {
    dark: css`
      background-color: ${color('white')};
      border-bottom-color: ${color('beige100')};
    `,
    light: css`
      background-color: transparent;
    `
  })}

  @media print {
    display: none;
  }
`;

/**
 * `Header` styled component.
 */

const Header = styled.div<NavThemeProps>`
  align-items: center;
  display: flex;
  height: ${theme('dimensions.navbarHeightMobile')}px;
  padding: 0 ${theme('grid.gutterMobile')}px;
  position: relative;

  ${media.min('md')`
    height: ${theme('dimensions.navbarBarHeight')}px;
    padding: 0 ${units(7)};
  `}

  /* A gradient cannot be transitioned, so it always exists and only fades. */
  &::before {
    background-image: linear-gradient(to bottom, ${color.transparentize('black', 0.2)}, ${color.transparentize('black', 0)});
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: opacity ${navTransition};
  }

  ${ifProp({ colorTheme: 'light' }, css`
    &::before {
      opacity: 1;
    }
  `, css`
    &::before {
      opacity: 0;
    }
  `)}
`;

/**
 * `Inner` styled component.
 */

const Inner = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex: 1 0 0;
  justify-content: space-between;
  min-width: 0;
  position: relative;
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(5)};
`;

/**
 * `LogoLink` styled component.
 */

const LogoLink = styled(RouterLink)<NavThemeProps>`
  line-height: 0;
  transition: ${navTransition};
  transition-property: color, opacity;

  ${switchProp('colorTheme', {
    dark: css`
      color: ${color('black')};
    `,
    light: css`
      color: ${color('white')};
    `
  })}

  &:focus-visible,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `MenuList` styled component.
 */

const MenuList = styled.div`
  display: none;

  ${media.min('md')`
    display: flex;
    gap: ${units(2)};
  `}
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(2)};
`;

/**
 * `DesktopActions` styled component.
 */

const DesktopActions = styled.div`
  display: none;

  ${media.min('md')`
    align-items: center;
    display: flex;
    gap: ${units(2)};
  `}
`;

/**
 * `SearchButton` styled component.
 */

const SearchButton = styled.button<NavThemeProps>`
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  line-height: 0;
  outline: none;
  padding: 0;
  transition: ${navTransition};
  transition-property: color, opacity;

  ${switchProp('colorTheme', {
    dark: css`
      color: ${color('black')};
    `,
    light: css`
      color: ${color('white')};
    `
  })}

  &:focus-visible,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `SearchSlot` styled component.
 *
 * On mobile the search icon only belongs to the open drawer, so it collapses
 * instead of popping in and out of the header.
 */

const SearchSlot = styled.div<{ isMobileOpen: boolean }>`
  overflow: hidden;
  transition: ${navTransition};
  transition-property: margin-right, max-width, opacity;

  ${ifProp('isMobileOpen', css`
    margin-right: 0;
    max-width: ${units(4)};
    opacity: 1;
  `, css`
    margin-right: -${units(2)};
    max-width: 0;
    opacity: 0;
  `)}

  ${media.min('md')`
    margin-right: 0;
    max-width: ${units(4)};
    opacity: 1;
  `}
`;

/**
 * `MobileActions` styled component.
 */

const MobileActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(2)};

  ${media.min('md')`
    display: none;
  `}
`;

/**
 * `MenuEntry` styled component.
 *
 * Groups a button with its own panel so that focus leaving the pair — by Tab
 * out of the last link, or away from the navbar — closes it.
 */

const MenuEntry = styled.div`
  display: flex;
`;

/**
 * `MegaMenuWrapper` styled component.
 */

const MegaMenuWrapper = styled.div<{ isOpen: boolean }>`
  display: none;

  /*
   * Positioned rather than in flow so that each panel can sit right after its
   * own button in the DOM. That is what keeps Tab and Shift+Tab symmetric
   * around the button that opens it.
   */
  ${media.min('md')`
    display: grid;
    grid-template-rows: 0fr;
    left: -${units(7)};
    position: absolute;
    right: -${units(7)};
    top: 100%;
    transition: grid-template-rows ${navTransition};
  `}

  ${ifProp('isOpen', css`
    ${media.min('md')`
      grid-template-rows: 1fr;
    `}
  `)}
`;

/**
 * `MegaMenuInner` styled component.
 */

const MegaMenuInner = styled.div<{ isOpen: boolean }>`
  /*
   * The panel shares its beige with several page backgrounds, so without a
   * shadow there is no edge telling the menu apart from the page under it.
   */
  border-bottom: 1px solid ${color.transparentize('neutralDarkest', borderOpacity)};
  box-shadow: 0 ${units(1)} ${units(4)} 0 ${color.transparentize('black', 0.16)};
  min-height: 0;
  overflow: hidden;
  transition-property: opacity, visibility;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  /*
   * Hiding it once collapsed is what keeps its links out of the tab order.
   * Without this the panel stays focusable while invisible, and it is marked
   * aria-hidden, which is an ARIA violation on its own.
   */
  ${ifProp('isOpen', css`
    opacity: 1;
    transition-delay: 0s, 0s;
    transition-duration: ${navDuration}, 0s;
    visibility: visible;
  `, css`
    opacity: 0;
    transition-delay: 0s, ${navDuration};
    transition-duration: ${navDuration}, 0s;
    visibility: hidden;
  `)}
`;

/**
 * `Backdrop` styled component.
 */

const Backdrop = styled.div`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: -1;
`;

/**
 * `Navbar` component.
 */

const Navbar = (): ReactElement => {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const router = useRouter();
  const menu = useMenu();
  const { theme: navbarTheme } = useNavbarThemeContext();
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const menuRefs = useRef<Partial<Record<MenuName, HTMLButtonElement>>>({});
  const [isScrolled, setScrolled] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<MenuName | null>(null);
  const [isMobileOpen, setMobileOpen] = useState<boolean>(false);
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
  const [displayedName, setDisplayedName] = useState<MenuName | null>(null);
  const { data: cards } = useNavbarCards({ enabled: !!displayedName });
  const colorTheme = useMemo<NavbarTheme>(() => {
    const isOverlay = navbarTheme?.colorTheme === 'light'
      && !isScrolled
      && !activeMenu
      && !isMobileOpen;

    return isOverlay ? 'light' : 'dark';
  }, [activeMenu, isMobileOpen, isScrolled, navbarTheme]);

  const clearTimers = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }
  }, []);

  const closeMegaMenu = useCallback(() => {
    clearTimers();
    setActiveMenu(null);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    clearTimers();

    closeTimeoutRef.current = setTimeout(() => setActiveMenu(null), closeDelay);
  }, [clearTimers]);

  const openMegaMenu = useCallback((name: MenuName) => {
    clearTimers();
    setActiveMenu(name);
  }, [clearTimers]);

  const scheduleOpen = useCallback((name: MenuName) => {
    clearTimers();

    setActiveMenu(current => {
      if (current) {
        return name;
      }

      openTimeoutRef.current = setTimeout(() => setActiveMenu(name), openDelay);

      return current;
    });
  }, [clearTimers]);

  // Closes the panel once focus leaves the button and its panel.
  const handleEntryBlur = useCallback((name: MenuName, event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setActiveMenu(current => current === name ? null : current);
  }, []);

  useBodyScroll({ off: isMobileOpen });

  useEffect(() => clearTimers, [clearTimers]);

  // Only re-renders when the threshold is actually crossed.
  useEffect(() => {
    const handleScroll = throttle(() => {
      setScrolled(window.scrollY > scrollThreshold);
    }, 100);

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      handleScroll.cancel();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Keeps the panel contents mounted while it collapses.
  useEffect(() => {
    if (activeMenu) {
      setDisplayedName(activeMenu);
    }
  }, [activeMenu]);

  useEffect(() => {
    const handleRouteChange = () => {
      setActiveMenu(null);
      setMobileOpen(false);
    };

    router.events.on('beforeHistoryChange', handleRouteChange);

    return () => {
      router.events.off('beforeHistoryChange', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (!activeMenu) {
      return;
    }

    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        closeMegaMenu();
        menuRefs.current[activeMenu]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeMenu, closeMegaMenu]);

  return (
    <Nav
      colorTheme={colorTheme}
      onMouseLeave={scheduleClose}
    >
      {activeMenu && (
        <Backdrop
          onClick={closeMegaMenu}
          onMouseEnter={scheduleClose}
        />
      )}

      <PreNavbar colorTheme={colorTheme} />

      <Header colorTheme={colorTheme}>
        <Inner>
          <Content>
            <LogoLink
              aria-label={'Revigrés'}
              colorTheme={colorTheme}
              href={routeResolver('home')}
              locale={locale}
            >
              <Svg
                icon={logoIcon}
                size={units(13)}
              />
            </LogoLink>

            <MenuList>
              {map(menu, entry => (
                <MenuEntry
                  key={entry.name}
                  onBlur={event => handleEntryBlur(entry.name, event)}
                >
                  <NavButton
                    aria-controls={`${megaMenuId}-${entry.name}`}
                    aria-expanded={activeMenu === entry.name}
                    colorTheme={colorTheme}
                    isActive={activeMenu === entry.name}
                    onClick={() => activeMenu === entry.name ? closeMegaMenu() : openMegaMenu(entry.name)}
                    onMouseEnter={() => scheduleOpen(entry.name)}
                    onMouseLeave={clearTimers}
                    ref={(element: HTMLButtonElement) => {
                      menuRefs.current[entry.name] = element;
                    }}
                  >
                    {entry.title}
                  </NavButton>

                  <MegaMenuWrapper
                    isOpen={activeMenu === entry.name}
                    onMouseEnter={clearTimers}
                  >
                    <MegaMenuInner
                      aria-hidden={activeMenu !== entry.name}
                      id={`${megaMenuId}-${entry.name}`}
                      isOpen={activeMenu === entry.name}
                    >
                      {displayedName === entry.name && (
                        <MegaMenu
                          card={cards?.[entry.name]}
                          menu={entry}
                        />
                      )}
                    </MegaMenuInner>
                  </MegaMenuWrapper>
                </MenuEntry>
              ))}
            </MenuList>
          </Content>

          <Actions>
            <SearchSlot isMobileOpen={isMobileOpen}>
              <SearchButton
                aria-label={translate('common:actions.search')}
                colorTheme={colorTheme}
                onClick={() => setSearchOpen(true)}
                type={'button'}
              >
                <Svg
                  icon={searchIcon}
                  size={units(4)}
                />
              </SearchButton>
            </SearchSlot>

            <DesktopActions>
              <NavbarButton
                colorTheme={colorTheme}
                href={routeResolver('recommend')}
                variant={'outlined'}
                {...isInternalRoute(routeResolver('recommend')) && { locale }}
              >
                {translate('menu:toolsMenu.recommender')}
              </NavbarButton>

              <NavbarButton
                colorTheme={colorTheme}
                href={routeResolver('whereToBuy')}
                variant={'fill'}
                {...isInternalRoute(routeResolver('whereToBuy')) && { locale }}
              >
                {translate('menu:whereToBuy')}
              </NavbarButton>
            </DesktopActions>

            <MobileActions>
              <HamburgerMenu
                colorTheme={colorTheme}
                isOpen={isMobileOpen}
                onClick={() => setMobileOpen(isOpen => !isOpen)}
              />
            </MobileActions>
          </Actions>
        </Inner>
      </Header>

      <MobileMenu
        isOpen={isMobileOpen}
        menu={menu}
        onClose={() => setMobileOpen(false)}
      />

      <Search
        isOpen={isSearchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </Nav>
  );
};

/**
 * Export `Navbar` component.
 */

export default Navbar;
